import React from 'react';
import { Wallet, Users } from 'lucide-react';

/**
 * Small inline receipt showing credit application breakdown for a ride.
 * Renders nothing if no credits were applied.
 *
 * Reads from ride.pricing_breakdown:
 *   - family_credit_applied
 *   - personal_credit_applied
 *   - original_price (for context)
 *
 * Also surfaces ride.paid_via when the ride is fully covered.
 */
const CreditReceipt = ({ ride, compact = false }) => {
  if (!ride) return null;
  const pb = ride.pricing_breakdown || {};
  const fam = Number(pb.family_credit_applied || 0);
  const personal = Number(pb.personal_credit_applied || 0);
  const total = fam + personal;
  if (total <= 0) return null;

  const original = Number(pb.original_price || ride.original_price || ride.estimated_price || 0);
  const riderPays = Number(pb.rider_pays !== undefined ? pb.rider_pays : (ride.estimated_price || 0));
  const paidVia = ride.paid_via;

  const fmt = (n) => `K${(n || 0).toLocaleString()}`;

  if (compact) {
    // Compact one-liner used in ride history
    const parts = [];
    if (fam > 0) parts.push(`${fmt(fam)} family`);
    if (personal > 0) parts.push(`${fmt(personal)} wallet`);
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-green-700 dark:text-green-400" data-testid="credit-receipt-compact">
        <Wallet className="w-3 h-3" />
        <span>Credit applied: {parts.join(' + ')}</span>
      </div>
    );
  }

  return (
    <div
      data-testid="credit-receipt"
      className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3 mb-3 text-xs"
    >
      <div className="flex items-center gap-2 mb-2">
        <Wallet className="w-4 h-4 text-green-600" />
        <span className="font-semibold text-green-800 dark:text-green-300">Credit applied to this ride</span>
      </div>

      {fam > 0 && (
        <div className="flex items-center justify-between text-green-700 dark:text-green-400">
          <span className="flex items-center gap-1.5">
            <Users className="w-3 h-3" />
            Family credit
          </span>
          <span className="font-semibold">−K{fam.toLocaleString()}</span>
        </div>
      )}
      {personal > 0 && (
        <div className="flex items-center justify-between text-green-700 dark:text-green-400 mt-1">
          <span className="flex items-center gap-1.5">
            <Wallet className="w-3 h-3" />
            Wallet credit
          </span>
          <span className="font-semibold">−K{personal.toLocaleString()}</span>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-green-200 dark:border-green-800 pt-1.5 mt-2 text-[11px] text-gray-700 dark:text-gray-300">
        <span>Fare K{original.toLocaleString()} · You paid K{riderPays.toLocaleString()}</span>
        {paidVia && (
          <span className="px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 font-semibold uppercase tracking-wider text-[9px]">
            Paid via {paidVia.replace('_', ' ')}
          </span>
        )}
      </div>
    </div>
  );
};

export default CreditReceipt;
